/**
 * OTActionMenu.jsx · v99.4.1 · Menú Lápiz (reemplaza "Editar OT")
 * Dropdown: reasignar técnico/tipo, horario, materiales, estado.
 * v101.16.0 · + Actualizar desde Odoo, Exportar, Tarea hija (desde toolbar).
 */
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Pencil, UserCog, Replace, CalendarClock, ArrowRightLeft,
  X, Loader2, AlertTriangle, Ban, Info, HelpCircle, PackageX,
  RefreshCw, Download, Plus, Repeat,
} from "lucide-react";
import api from "../lib/api";
import CancelReasonPicker from "./CancelReasonPicker";   // v101.188.0
import BodyPortal from "./ui/BodyPortal";
import { useAuth } from "../lib/auth";

const LANES = [
  { key: "nuevas",          label: "Nuevas",          desc: "Quita técnico y horario",                          enabled: true },
  { key: "revision",        label: "Revisión",        desc: "Envía a asignación manual",                        enabled: true },
  { key: "asignadas",       label: "Asignadas",       desc: "Requiere reasignar técnico",                       enabled: "assign" },
  { key: "en_ejecucion",    label: "En ejecución",    desc: "Solo supervisor/admin",                             enabled: "admin" },
  { key: "canceladas",      label: "Canceladas",      desc: "Cancelar la OT",                                    enabled: true },
  { key: "infraestructura", label: "Infraestructura",  desc: "Depende del tipo de actividad",                    enabled: false },
  { key: "fiscalizacion",   label: "Fiscalización",   desc: "Depende del tipo de actividad",                     enabled: false },
  { key: "realizadas",      label: "Realizadas/Cierre",desc: "Se alcanza desde la app del técnico con evidencias", enabled: false },
];

export default function OTActionMenu({
  ot, techs, onUpdated, className,
  // v101.16.0 · Acciones migradas desde la toolbar del detalle
  canRefreshOdoo = false,
  odooRefreshing = false,
  onRefreshOdoo,
  canExport = false,
  onExport,
  canCreateChild = false,
  onCreateChild,
  // v101.200.0 · Acciones de la barra que pasan al lápiz (Imprimir, Actualizar GPS, Actualizar dirección…)
  extraItems = [],
}) {
  const { user } = useAuth();
  const role = user?.role || "";
  const canAction = ["super_admin", "supervisor", "admin", "admin_zone", "agendador"].includes(role);
  const isAdminLevel = ["super_admin", "supervisor", "admin"].includes(role);

  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // "tech" | "solution" | "schedule" | "lane"
  const menuRef = useRef(null);
  const dropRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, up: false });

  const extras = (extraItems || []).filter(Boolean);
  const showExtra = !!(canRefreshOdoo || canExport || canCreateChild || extras.length);

  // v92.7.0 · Posición del desplegable (fijo, en <body>) según el botón; se reubica con scroll/resize/zoom
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = menuRef.current?.getBoundingClientRect();
      if (!r) return;
      // v101.16.0 · Menú más alto con acciones extra
      const W = 224, H = (showExtra ? 340 : 240) + extras.length * 36;
      const up = window.innerHeight - r.bottom < H && r.top > H;
      setPos({
        top: up ? r.top - 4 : r.bottom + 4,
        left: Math.max(8, Math.min(r.right - W, window.innerWidth - W - 8)),
        up,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open, showExtra]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inBtn = menuRef.current && menuRef.current.contains(e.target);
      const inDrop = dropRef.current && dropRef.current.contains(e.target);
      if (!inBtn && !inDrop) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!ot || (!canAction && !extras.length)) return null;

  const openModal = (m) => { setOpen(false); setModal(m); };

  const hasTech = !!ot.assigned_tech_id;
  const isProvisioned = !!ot.cpe_supply_completed;

  return (
    <>
      <div className={`relative ${className || ""}`} ref={menuRef}>
        <button
          onClick={() => setOpen(!open)}
          data-testid="btn-ot-action-menu"
          title="Acciones de la OT"
          className="p-2 rounded-lg bg-[#0A3747] text-white hover:bg-[#135166] transition-all"
        >
          <Pencil size={15} />
        </button>
        {open && (
          <BodyPortal>
          <div
            ref={dropRef}
            className="afc-floating-menu fixed w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-1 animate-in fade-in slide-in-from-top-2"
            style={{ top: pos.top, left: pos.left, transform: pos.up ? "translateY(-100%)" : undefined }}
            data-testid="ot-action-dropdown"
          >
            {canAction && (<>
            <MenuItem
              icon={UserCog} label="Reasignar técnico"
              onClick={() => openModal("tech")}
              testid="action-reassign-tech"
            />
            <MenuItem
              icon={Replace} label="Reasignar tipo de solución"
              onClick={() => openModal("solution")}
              testid="action-reassign-solution"
            />
            <MenuItem
              icon={CalendarClock} label="Cambio de horario"
              onClick={() => hasTech ? openModal("schedule") : toast.info("Primero asigne un técnico")}
              disabled={!hasTech}
              testid="action-change-schedule"
            />
            <MenuItem
              icon={PackageX} label="Materiales · liberar / cambiar serie"
              // v101.49.1 · Si está aprovisionada: abrir modal igual (muestra bloqueo); no liberar a ciegas
              onClick={() => openModal("materials")}
              testid="action-release-materials"
            />
            <div className="border-t border-gray-100 my-1" />
            <MenuItem
              icon={ArrowRightLeft} label="Cambio de estado"
              onClick={() => openModal("lane")}
              testid="action-change-lane"
            />
            </>)}
            {/* v101.16.0 · Acciones que estaban en la toolbar del detalle */}
            {showExtra && <div className="border-t border-gray-100 my-1" />}
            {canRefreshOdoo && (
              <MenuItem
                icon={RefreshCw}
                label={odooRefreshing ? "Actualizando…" : "Actualizar desde Odoo"}
                onClick={() => { setOpen(false); onRefreshOdoo?.(); }}
                disabled={odooRefreshing}
                testid="action-refresh-from-odoo"
              />
            )}
            {canExport && (
              <MenuItem
                icon={Download}
                label="Exportar"
                onClick={() => { setOpen(false); onExport?.(); }}
                testid="action-export-ot"
              />
            )}
            {canCreateChild && (
              <MenuItem
                icon={Plus}
                label="Tarea hija"
                onClick={() => { setOpen(false); onCreateChild?.(); }}
                testid="action-create-child-ot"
              />
            )}
            {extras.length > 0 && <div className="border-t border-gray-100 my-1" />}
            {extras.map((x) => (
              <MenuItem key={x.testid || x.label} icon={x.icon} label={x.label}
                onClick={() => { setOpen(false); x.onClick?.(); }} disabled={x.disabled} testid={x.testid} />
            ))}
          </div>
          </BodyPortal>
        )}
      </div>

      {/* Modals */}
      {modal === "tech" && (
        <ReassignTechModal ot={ot} techs={techs} onClose={() => setModal(null)} onUpdated={onUpdated} />
      )}
      {modal === "solution" && (
        <ReassignSolutionModal ot={ot} onClose={() => setModal(null)} onUpdated={onUpdated}
          onNeedRelease={() => setModal("materials_then_solution")} />
      )}
      {(modal === "materials" || modal === "materials_then_solution") && (
        <ReleaseMaterialsModal ot={ot}
          onClose={() => setModal(modal === "materials_then_solution" ? "solution" : null)}
          onReleased={(opts) => {
            onUpdated?.();
            if (opts?.keepOpen) return;   // v101.79.0 · quitar un solo material: el modal sigue abierto
            setModal(modal === "materials_then_solution" ? "solution" : null);
          }} />
      )}
      {modal === "schedule" && (
        <ChangeScheduleModal ot={ot} onClose={() => setModal(null)} onUpdated={onUpdated} />
      )}
      {modal === "lane" && (
        <ChangeLaneModal ot={ot} isAdminLevel={isAdminLevel} isProvisioned={isProvisioned} onClose={() => setModal(null)} onUpdated={onUpdated} />
      )}
    </>
  );
}

function MenuItem({ icon: Icon, label, onClick, disabled, testid }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left transition-colors ${
        disabled ? "text-gray-300 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50"
      }`}
      data-testid={testid}
    >
      <Icon size={14} className={disabled ? "text-gray-300" : "text-[#0A3747]"} />
      <span className="font-medium">{label}</span>
    </button>
  );
}

/* ─── Modal: Reasignar Técnico ─── */
function ReassignTechModal({ ot, techs, onClose, onUpdated }) {
  const [techId, setTechId] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(null);

  // v101.6.0 · API /ot_smart/technicians expone name + branch_id (no full_name / branch_office_id)
  const branchTechs = (techs || []).filter((t) => {
    if (!ot.branch_office_id) return true;
    const techBranch = t.branch_id ?? t.branch_office_id;
    return techBranch == null || String(techBranch) === String(ot.branch_office_id);
  });

  const submit = async (force = false) => {
    if (!techId) return toast.error("Seleccione un técnico");
    if (!reason.trim()) return toast.error("Motivo es requerido");
    setLoading(true);
    setWarning(null);
    try {
      // v101.9.0 · force también cubre split de suscripción (mismo técnico esperado)
      await api.post(`/v1/ot_smart/${ot.odoo_task_id}/assign`, {
        technician_id: techId,
        scheduled_at: ot.scheduled_at || null,
        notes: reason,
        force_outside_shift: force,
        force_reason: force ? reason : undefined,
        force_subscription_split: force,
      });
      toast.success("Técnico reasignado");
      onUpdated?.();
      onClose();
    } catch (e) {
      const resp = e?.response?.data || {};
      const detail = resp.detail || "";
      const code = resp.code || "";
      if (
        code === "OUTSIDE_SHIFT" || code === "SUBSCRIPTION_SIBLING_TECH"
        || detail.includes("fuera de turno") || detail.includes("outside")
        || detail.includes("shift") || detail.includes("misma suscripción")
      ) {
        setWarning(detail);
      } else {
        toast.error(detail || "Error al reasignar");
      }
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Reasignar técnico" onClose={onClose} testid="modal-reassign-tech">
      <label className="block text-xs font-semibold text-gray-600 mb-1">Técnico</label>
      <select
        value={techId} onChange={(e) => setTechId(e.target.value)}
        className="w-full border rounded px-2 py-1.5 text-xs mb-3"
        data-testid="select-tech"
      >
        <option value="">— Seleccionar —</option>
        {branchTechs.map((t) => {
          // v101.6.0 · name/email + branch_name hidratado por API (UUID → nombre legible)
          const label = t.name || t.full_name || t.email || "Sin nombre";
          const hint = t.branch_name || (t.email && t.email !== label ? t.email : null);
          return (
            <option key={t.id} value={t.id}>
              {hint ? `${label} (${hint})` : label}
            </option>
          );
        })}
      </select>
      <label className="block text-xs font-semibold text-gray-600 mb-1">Motivo</label>
      <input
        value={reason} onChange={(e) => setReason(e.target.value)}
        className="w-full border rounded px-2 py-1.5 text-xs mb-3" placeholder="Motivo de la reasignación"
        data-testid="input-reason"
      />
      {warning && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <div>{warning}</div>
            <button
              onClick={() => submit(true)}
              disabled={loading}
              className="mt-1.5 px-3 py-1 rounded bg-amber-600 text-white text-[10px] font-bold hover:bg-amber-700"
              data-testid="btn-force-assign"
            >
              Asignar de todos modos
            </button>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border hover:bg-gray-50">Cancelar</button>
        <button onClick={() => submit(false)} disabled={loading}
          className="px-3 py-1.5 text-xs rounded bg-[#0A3747] text-white hover:bg-[#135166] disabled:opacity-50 flex items-center gap-1.5"
          data-testid="btn-confirm-reassign"
        >
          {loading && <Loader2 size={12} className="animate-spin" />} Reasignar
        </button>
      </div>
    </ModalShell>
  );
}

/* ─── Modal: Reasignar Tipo de Solución ─── */
function ReassignSolutionModal({ ot, onClose, onUpdated, onNeedRelease }) {
  const [needRelease, setNeedRelease] = useState(false);
  const [nodes, setNodes] = useState([]);
  // v101.121.0 · Solo tipos cuyo «Aplica a» incluye el tipo de cliente de la OT
  const [clientInfo, setClientInfo] = useState({ segment: "", hidden: 0, hiddenPlan: 0, red: null });
  const [nodeId, setNodeId] = useState("");
  const [reason, setReason] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingNodes, setLoadingNodes] = useState(true);
  // v93.9.0 · Buscador + árbol jerárquico desplegable
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  // v101.137.0 · Filtro por tipo de cliente (contrato) y tipo de plan; «Ver todos» para todos los roles
  const canSeeAll = true;

  // v101.82.0 · No se puede cambiar si la OT está aprovisionada o tiene materiales cargados
  const [block, setBlock] = useState(null);
  useEffect(() => {
    api.get(`/v1/ot_smart/${ot.odoo_task_id}/change-solution-node/check`)
      .then(({ data }) => {
        const b = data && data.allowed === false ? data : null;
        setBlock(b);
        if (b && b.materials && !b.provisioned) setNeedRelease(true);
      })
      .catch(() => setBlock(null));
    /* eslint-disable-next-line */
  }, [ot.odoo_task_id]);

  useEffect(() => {
    setNodeId("");
    setLoadingNodes(true);
    (async () => {
      try {
        const { data } = await api.get(`/v1/ot_smart/${ot.odoo_task_id}/compatible-nodes${showAll ? "?all=true" : ""}`);
        setNodes(data.nodes || []);
        setClientInfo({ segment: data.client_segment || "", hidden: Number(data.hidden_by_client_type || 0),
          hiddenPlan: Number(data.hidden_by_plan || 0), red: data.plan_red || null });   // v101.137.0
      } catch {
        setNodes([]);
        setClientInfo({ segment: "", hidden: 0, hiddenPlan: 0, red: null });
      } finally { setLoadingNodes(false); }
    })();
  }, [ot.odoo_task_id, showAll]);

  const selectedNode = nodes.find((n) => n.id === nodeId);

  const submit = async (force = false) => {
    if (!nodeId) return toast.error("Seleccione un tipo de solución");
    if (reason.trim().length < 5) return toast.error("El motivo debe tener al menos 5 caracteres");
    setLoading(true);
    try {
      await api.put(`/v1/ot_smart/${ot.odoo_task_id}/change-solution-node`, {
        new_node_id: nodeId,
        reason: reason.trim(),
        force,
        all_types: showAll,   // v101.137.0 · elegido desde «Ver todos»
      });
      toast.success(force ? "Tipo de solución forzado correctamente" : "Tipo de solución actualizado");
      onUpdated?.();
      onClose();
    } catch (e) {
      const det = String(e?.response?.data?.detail || "Error al cambiar tipo de solución");
      if (/libere los materiales/i.test(det)) setNeedRelease(true);   // v100.6.0
      toast.error(det);
    } finally { setLoading(false); }
  };

  // v93.9.0 · Árbol por jerarquía (ancestros) con búsqueda sin tildes ni mayúsculas
  const norm = (t) => (t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const nq = norm(q.trim());
  const visible = nq
    ? nodes.filter((n) => norm(n.name).includes(nq) || (n.ancestors || []).some((a) => norm(a).includes(nq)))
    : nodes;
  const tree = { children: {}, items: [] };
  visible.forEach((n) => {
    let cur = tree;
    (n.ancestors && n.ancestors.length ? n.ancestors : [n.category_name || "Sin categoría"]).forEach((a) => {
      cur.children[a] = cur.children[a] || { children: {}, items: [] };
      cur = cur.children[a];
    });
    cur.items.push(n);
  });
  const countItems = (t) => t.items.length + Object.values(t.children).reduce((acc, c) => acc + countItems(c), 0);
  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));
  const renderTree = (t, prefix = "", level = 0) => (
    <>
      {Object.entries(t.children).map(([name, child]) => {
        const key = prefix ? `${prefix} > ${name}` : name;
        const open = !!nq || !!expanded[key];
        return (
          <div key={key}>
            <button type="button" onClick={() => toggle(key)}
              className="w-full flex items-center gap-1 py-1 text-left text-[11px] font-semibold text-[#0A3747] hover:bg-gray-50 rounded"
              style={{ paddingLeft: 6 + level * 12 }} data-testid={`tree-group-${key}`}>
              <span className="w-3 inline-block">{open ? "▾" : "▸"}</span>{name}
              <span className="ml-auto text-[9px] text-gray-400 pr-1">{countItems(child)}</span>
            </button>
            {open && renderTree(child, key, level + 1)}
          </div>
        );
      })}
      {t.items.map((n) => (
        <button type="button" key={n.id} onClick={() => setNodeId(n.id)}
          className={`w-full text-left py-1 text-[11px] rounded ${nodeId === n.id ? "bg-[#0A3747] text-white" : "hover:bg-blue-50 text-gray-700"}`}
          style={{ paddingLeft: 18 + level * 12 }} data-testid={`tree-node-${n.id}`}>
          {n.name}{n.has_children ? " · nivel con subtipos" : ""}{n.code_mismatch ? ` ⚠ (${n.odoo_activity_type || "sin código"})` : ""}
        </button>
      ))}
    </>
  );

  return (
    <ModalShell title={<span className="flex items-center gap-2">Reasignar tipo de solución<a href="/centro-documentacion#tipos-solucion#8" title="Manual: Uso desde la OT" className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#0A3747]/10 hover:bg-[#0A3747]/20 transition" data-testid="help-reassign-solution"><HelpCircle size={12} className="text-[#0A3747]" /></a></span>} onClose={onClose} testid="modal-reassign-solution">
      {loadingNodes ? (
        <div className="flex items-center gap-2 text-xs text-gray-400 py-4"><Loader2 size={14} className="animate-spin" /> Cargando opciones…</div>
      ) : nodes.length === 0 ? (
        <div className="text-xs text-gray-500 py-4" data-testid="no-compatible-nodes">
          No hay tipos de solución que apliquen
          {clientInfo.segment ? <> al cliente <b>{clientInfo.segment}</b></> : null}
          {clientInfo.red ? <> con plan de <b>{clientInfo.red === "radio_enlace" ? "radio" : "fibra"}</b></> : null}.
          Use «Ver todos» para elegir cualquier tipo.
          {(clientInfo.hidden > 0 || clientInfo.hiddenPlan > 0) && (
            <div className="mt-1 text-[10px] text-gray-400">
              Ocultos: {clientInfo.hidden} por «Aplica a» (tipo de cliente) · {clientInfo.hiddenPlan} por tipo de plan.
            </div>
          )}
        </div>
      ) : (
        <>
          {!showAll && (clientInfo.segment || clientInfo.red) && (
            <div className="mb-2 px-2 py-1 rounded bg-blue-50 border border-blue-100 text-[10px] text-[#0A3747]" data-testid="client-type-filter">
              Cliente: <b>{clientInfo.segment || "—"}</b> · Plan: <b>{clientInfo.red === "radio_enlace" ? "Radio" : clientInfo.red ? "Fibra" : "—"}</b>
              {" "}· solo tipos que aplican a este cliente y plan
              {(clientInfo.hidden + clientInfo.hiddenPlan) > 0 ? ` (${clientInfo.hidden + clientInfo.hiddenPlan} ocultos)` : ""}
            </div>
          )}
          {showAll && (
            <div className="mb-2 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-[10px] text-amber-900" data-testid="all-types-banner">
              Mostrando TODOS los tipos de solución (sin filtro de cliente ni plan).
            </div>
          )}
          <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo de solución ({nodes.length} opciones)</label>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar tipo de solución o categoría…"
            className="w-full border rounded px-2 py-1.5 text-xs mb-1.5" data-testid="search-solution-node" />
          <div className="border rounded max-h-64 overflow-y-auto p-1 mb-2" data-testid="tree-solution-node">
            {visible.length === 0
              ? <div className="text-[11px] text-gray-400 py-2 text-center">Sin coincidencias</div>
              : renderTree(tree)}
          </div>
          {selectedNode && (
            <div className="text-[10px] text-gray-500 mb-2" data-testid="selected-solution-path">
              Seleccionado: <b>{[...(selectedNode.ancestors || []), selectedNode.name].join(" › ")}</b>
            </div>
          )}
          <label className="block text-xs font-semibold text-gray-600 mb-1 mt-1">Motivo del cambio *</label>
          <input
            type="text" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Mín. 5 caracteres — ej: Corrección de tipo por error de asignación"
            className="w-full border rounded px-2 py-1.5 text-xs mb-3"
            data-testid="input-solution-reason"
            minLength={5} required
          />
          {selectedNode?.code_mismatch && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-xs text-amber-800 flex items-start gap-2" data-testid="warning-code-mismatch">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-amber-600" />
              <div>
                <div className="text-[10px]">El tipo elegido no coincide con la actividad de Odoo ({selectedNode.odoo_activity_type || "sin código"} ≠ {ot.activity_type_code})</div>
                <button
                  onClick={() => submit(true)}
                  disabled={loading || reason.trim().length < 5 || !!block}
                  className="mt-1.5 px-3 py-1 rounded bg-amber-600 text-white text-[10px] font-bold hover:bg-amber-700 disabled:opacity-50"
                  data-testid="btn-force-solution"
                >
                  Cambiar de todos modos
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {block && (
        <div className="mb-3 p-2.5 rounded border border-red-300 bg-red-50 text-xs text-red-800 font-medium" data-testid="change-solution-blocked">
          {block.message}
        </div>
      )}
      {needRelease && onNeedRelease && (
        <div className="mb-3 p-2 rounded border border-red-200 bg-red-50 text-[11px] text-red-800 flex items-center gap-2" data-testid="need-release-banner">
          <PackageX size={14} className="flex-shrink-0" />
          <span className="flex-1">Esta OT tiene materiales registrados. Libérelos para poder cambiar el tipo de solución.</span>
          <button onClick={onNeedRelease} className="px-2 py-1 rounded bg-red-600 text-white font-bold" data-testid="btn-go-release-materials">
            Liberar materiales
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div>
          {canSeeAll && (
            <button onClick={() => setShowAll(!showAll)}
              className="text-[10px] text-blue-600 hover:underline"
              data-testid="btn-toggle-all-nodes"
            >
              {showAll ? "Solo compatibles" : "Ver todos"}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border hover:bg-gray-50">Cancelar</button>
          <button onClick={() => submit(false)} disabled={loading || !nodeId || reason.trim().length < 5 || !!selectedNode?.code_mismatch || !!block}
            className="px-3 py-1.5 text-xs rounded bg-[#0A3747] text-white hover:bg-[#135166] disabled:opacity-50 flex items-center gap-1.5"
            data-testid="btn-confirm-solution"
          >
            {loading && <Loader2 size={12} className="animate-spin" />} Cambiar
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ─── v100.6.0 · Modal: Liberar materiales (solo web) ─── */
function ReleaseMaterialsModal({ ot, onClose, onReleased }) {
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  // v101.79.0 · Quitar UN material: ✕ por renglón → comentario obligatorio → confirmación explícita
  const [rm, setRm] = useState(null);          // { item, step: "comment" | "confirm" }
  const [rmNote, setRmNote] = useState("");
  const [rmBusy, setRmBusy] = useState(false);
  // v101.154.0 · Cambiar serie (también en fiscalización): elegir de la bodega del técnico → comentario → confirmar
  const [cs, setCs] = useState(null);          // { item, step: "pick" | "confirm", options, loading }
  const [csNew, setCsNew] = useState("");
  const [csNote, setCsNote] = useState("");
  const [csBusy, setCsBusy] = useState(false);
  const startChangeSerial = async (m) => {
    if (!m.serial_changeable) { toast.error(m.serial_change_block || "No se puede cambiar la serie."); return; }
    setRm(null); setCsNew(""); setCsNote("");
    setCs({ item: m, step: "pick", options: [], loading: true });
    try {
      const { data: o } = await api.get(`/v1/ot_smart/${ot.odoo_task_id}/material-serial-options`, {
        params: { serial: m.serial, ...(m.product_id ? { product_id: m.product_id } : {}) },
      });
      setCs((c) => (c ? { ...c, options: o?.items || [], loading: false } : c));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar las series de la bodega.");
      setCs((c) => (c ? { ...c, loading: false } : c));
    }
  };
  const doChangeSerial = async () => {
    const m = cs?.item;
    if (!m) return;
    setCsBusy(true);
    try {
      const { data: r } = await api.post(`/v1/ot_smart/${ot.odoo_task_id}/change-material-serial`, {
        submission_id: m.submission_id, index: m._idx, product_id: m.product_id ?? null,
        serial: m.serial, new_serial: csNew, reason: csNote.trim(),
      });
      toast.success(`Serie cambiada: ${r.from} → ${r.to}. Vuelva a intentar el cierre si había fallado.`);
      setCs(null); setCsNew(""); setCsNote("");
      loadMaterials();
      onReleased?.({ keepOpen: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar la serie.");
      setCs((c) => (c ? { ...c, step: "pick" } : c));
    } finally { setCsBusy(false); }
  };
  const loadMaterials = () => {
    api.get(`/v1/ot_smart/${ot.odoo_task_id}/materials`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], count: 0, can_release: false, blocked_reason: "No se pudieron cargar los materiales" }));
  };
  useEffect(() => { loadMaterials(); /* eslint-disable-next-line */ }, [ot.odoo_task_id]);
  const itemName = (m) => m?.product_name || m?.nombre || m?.product_code || m?.codigo_producto_odoo || "material";
  const itemQty = (m) => m?.quantity ?? m?.cantidad ?? m?.qty ?? "—";
  const startRemove = (m) => {
    if (!m.removable) { toast.error(m.remove_block || "Este material no se puede quitar."); return; }
    setRm({ item: m, step: "comment" }); setRmNote("");
  };
  const doRemove = async () => {
    const m = rm?.item;
    if (!m) return;
    setRmBusy(true);
    try {
      await api.post(`/v1/ot_smart/${ot.odoo_task_id}/release-material-item`, {
        submission_id: m.submission_id, index: m._idx, product_id: m.product_id ?? null,
        serial: m.serial || null, reason: rmNote.trim(),
      });
      toast.success(`Se quitó «${itemName(m)}» de la OT.`);
      setRm(null); setRmNote("");
      loadMaterials();
      onReleased?.({ keepOpen: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo quitar el material.");
      setRm((r) => (r ? { ...r, step: "comment" } : r));
    } finally { setRmBusy(false); }
  };
  const submit = async () => {
    if (reason.trim().length < 5) return toast.error("El motivo debe tener al menos 5 caracteres");
    if (!window.confirm(`¿Liberar ${data?.count || 0} material(es) de la OT ${ot.task_number || ot.odoo_task_id}? El técnico deberá registrarlos de nuevo si corresponde.`)) return;
    setLoading(true);
    try {
      const { data: r } = await api.post(`/v1/ot_smart/${ot.odoo_task_id}/release-materials`, { reason: reason.trim() });
      toast.success(r.released ? `${r.released} material(es) liberados` : (r.message || "Sin materiales para liberar"));
      onReleased?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron liberar los materiales");
    } finally { setLoading(false); }
  };
  return (
    <ModalShell title={<span className="flex items-center gap-2"><PackageX size={16} /> Materiales · liberar / cambiar serie</span>} onClose={onClose} testid="modal-release-materials">
      {!data ? (
        <div className="flex items-center gap-2 text-xs text-gray-400 py-4"><Loader2 size={14} className="animate-spin" /> Cargando materiales…</div>
      ) : (
        <>
          <p className="text-xs text-gray-600 mb-2">
            Quita los materiales que el técnico registró en esta OT. Se guarda una copia en el historial de la OT.
            Úselo, por ejemplo, para poder cambiar el tipo de solución.
            {/* v101.49.1 */}
            {data.cpe_supply_completed
              ? " Si la OT está aprovisionada, debe cancelar el aprovisionamiento antes."
              : ""}
          </p>
          {data.blocked_reason && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-2 font-medium" data-testid="release-materials-blocked">
              {data.blocked_reason}
              {(data.items || []).some((m) => m.serial_changeable) && (
                <div className="font-normal text-[#0A3747] mt-1">Para corregir la serie de un equipo use el botón «Cambiar serie» (⇄) del renglón.</div>
              )}
            </div>
          )}
          <div className="border rounded max-h-56 overflow-y-auto mb-3" data-testid="release-materials-list">
            {data.items.length === 0 ? (
              <div className="text-[11px] text-gray-400 p-3 text-center">La OT no tiene materiales registrados.</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50"><tr>
                  <th className="text-left px-2 py-1">Material</th>
                  <th className="text-right px-2 py-1">Cant.</th>
                  <th className="text-left px-2 py-1">Serie</th>
                  <th className="px-1 py-1 w-6"></th>
                </tr></thead>
                <tbody>
                  {data.items.map((m, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1">{m.product_name || m.nombre || m.product_code || m.codigo_producto_odoo || "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{m.quantity ?? m.cantidad ?? m.qty ?? "—"}</td>
                      <td className="px-2 py-1 font-mono">{m.serial || m.serie || "—"}</td>
                      <td className="px-1 py-1 text-center whitespace-nowrap">
                        {/* v101.154.0 · Cambiar serie (elegir de la bodega del técnico) */}
                        {(m.serial || m.serie) && (
                          <button type="button" onClick={() => startChangeSerial(m)} disabled={!!rm || !!cs}
                            title={m.serial_changeable ? "Cambiar serie" : (m.serial_change_block || "No se puede cambiar la serie")}
                            className={`p-1 rounded ${m.serial_changeable ? "text-[#0A3747] hover:bg-sky-50" : "text-gray-300 cursor-not-allowed"}`}
                            data-testid={`btn-change-serial-${i}`}>
                            <Repeat size={13} />
                          </button>
                        )}
                        {/* v101.79.0 · Quitar solo este material */}
                        <button type="button" onClick={() => startRemove(m)} disabled={!!rm || !!cs}
                          title={m.removable ? "Quitar este material" : (m.remove_block || "No se puede quitar")}
                          className={`p-1 rounded ${m.removable ? "text-red-600 hover:bg-red-50" : "text-gray-300 cursor-not-allowed"}`}
                          data-testid={`btn-remove-material-${i}`}>
                          <X size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {cs && (
            <div className="border-2 border-sky-300 bg-sky-50 rounded p-3 mb-3" data-testid="change-serial-panel">
              <div className="text-xs font-bold text-[#0A3747] mb-1">
                Cambiar serie de «{itemName(cs.item)}» · actual {cs.item.serial}
              </div>
              {cs.step === "pick" ? (
                <>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Serie nueva (bodega del técnico)</label>
                  {cs.loading ? (
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-2"><Loader2 size={12} className="animate-spin" /> Cargando series…</div>
                  ) : cs.options.length === 0 ? (
                    <div className="text-[11px] text-red-700 mb-2" data-testid="change-serial-none">
                      El técnico no tiene en su bodega otra serie de este equipo (ni de la misma categoría). Si recibió el
                      equipo, que confirme la transferencia en la app y vuelva a abrir esta ventana.
                    </div>
                  ) : (
                    <select value={csNew} onChange={(e) => setCsNew(e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-xs mb-2 font-mono" data-testid="select-change-serial">
                      <option value="">-- Seleccionar serie --</option>
                      {cs.options.map((o) => (
                        <option key={o.serial} value={o.serial}>
                          {o.serial} · {(o.product_name || "").split(" / ").pop()}{o.same_product ? "" : " (otro modelo)"}
                        </option>
                      ))}
                    </select>
                  )}
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">¿Por qué se cambia la serie? (obligatorio)</label>
                  <textarea value={csNote} onChange={(e) => setCsNote(e.target.value)} rows={2}
                    placeholder="Ej.: el técnico registró la serie de otro equipo; el instalado es el de la etiqueta"
                    className="w-full border rounded px-2 py-1.5 text-xs mb-2" data-testid="input-change-serial-note" />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setCs(null)}
                      className="px-3 py-1.5 text-xs rounded border bg-white hover:bg-gray-50">Cancelar</button>
                    <button type="button" disabled={!csNew || csNote.trim().length < 5}
                      onClick={() => setCs({ ...cs, step: "confirm" })}
                      className="px-3 py-1.5 text-xs rounded bg-[#0A3747] text-white disabled:opacity-50"
                      data-testid="btn-change-serial-next">
                      Cambiar serie
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[12px] text-gray-800 mb-2" data-testid="change-serial-confirm">
                    <strong>¿Confirma el cambio de serie {cs.item.serial} → {csNew}?</strong>
                    <br />Comentario: «{csNote.trim()}»
                    <br /><span className="text-[11px] text-gray-600">Se cambia en los materiales de la OT y en la serie que se
                      envía a Odoo al cerrar. {data.cpe_supply_completed ? "La serie aprovisionada en la red NO cambia (si el equipo físico es otro, cancele el aprovisionamiento)." : ""}
                      Queda registrado en el historial de la OT con su usuario.</span>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setCs({ ...cs, step: "pick" })} disabled={csBusy}
                      className="px-3 py-1.5 text-xs rounded border bg-white hover:bg-gray-50">No, volver</button>
                    <button type="button" onClick={doChangeSerial} disabled={csBusy}
                      className="px-3 py-1.5 text-xs rounded bg-[#0A3747] text-white disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="btn-change-serial-confirm">
                      {csBusy && <Loader2 size={12} className="animate-spin" />} Sí, cambiar serie
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {rm && (
            <div className="border-2 border-red-300 bg-red-50 rounded p-3 mb-3" data-testid="remove-material-panel">
              <div className="text-xs font-bold text-red-800 mb-1">
                Quitar «{itemName(rm.item)}» · cantidad {itemQty(rm.item)}{rm.item.serial ? ` · serie ${rm.item.serial}` : ""}
              </div>
              {rm.step === "comment" ? (
                <>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    ¿Por qué se quita este material? (obligatorio)
                  </label>
                  <textarea value={rmNote} onChange={(e) => setRmNote(e.target.value)} rows={2} autoFocus
                    placeholder="Ej.: el técnico lo registró por error; no se usó en la instalación"
                    className="w-full border rounded px-2 py-1.5 text-xs mb-2" data-testid="input-remove-material-note" />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setRm(null); setRmNote(""); }}
                      className="px-3 py-1.5 text-xs rounded border bg-white hover:bg-gray-50">Cancelar</button>
                    <button type="button" disabled={rmNote.trim().length < 5}
                      onClick={() => setRm({ ...rm, step: "confirm" })}
                      className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      data-testid="btn-remove-material-next">
                      Quitar material
                    </button>
                  </div>
                  {rmNote.trim().length > 0 && rmNote.trim().length < 5 && (
                    <div className="text-[10px] text-red-700 mt-1">Escriba al menos 5 caracteres.</div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-[12px] text-gray-800 mb-2" data-testid="remove-material-confirm">
                    <strong>¿Confirma que desea quitar este material de la OT?</strong>
                    <br />Comentario: «{rmNote.trim()}»
                    <br /><span className="text-[11px] text-gray-600">Queda registrado en el historial de la OT con su usuario.
                      {rm.item.serial ? " El equipo vuelve a estar libre para registrarse de nuevo." : ""}</span>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setRm({ ...rm, step: "comment" })} disabled={rmBusy}
                      className="px-3 py-1.5 text-xs rounded border bg-white hover:bg-gray-50">No, volver</button>
                    <button type="button" onClick={doRemove} disabled={rmBusy}
                      className="px-3 py-1.5 text-xs rounded bg-red-700 text-white hover:bg-red-800 disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="btn-remove-material-confirm">
                      {rmBusy && <Loader2 size={12} className="animate-spin" />} Sí, quitar material
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {data.can_release && !rm && !cs && (
            <>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Motivo *</label>
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Mín. 5 caracteres — ej: Cambio de tipo de solución"
                className="w-full border rounded px-2 py-1.5 text-xs mb-3" data-testid="input-release-reason" />
            </>
          )}
        </>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border hover:bg-gray-50">Cerrar</button>
        {data?.can_release && (
          <button onClick={submit} disabled={loading || reason.trim().length < 5}
            className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="btn-confirm-release-materials">
            {loading && <Loader2 size={12} className="animate-spin" />} Liberar materiales
          </button>
        )}
      </div>
    </ModalShell>
  );
}

/* ─── Modal: Cambio de Horario ─── */
function ChangeScheduleModal({ ot, onClose, onUpdated }) {
  const currentDate = ot.scheduled_at ? new Date(ot.scheduled_at).toISOString().slice(0, 16) : "";
  const [datetime, setDatetime] = useState(currentDate);
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(null);

  const submit = async (force = false) => {
    if (!datetime) return toast.error("Seleccione fecha y hora");
    setLoading(true);
    setWarning(null);
    try {
      await api.post(`/v1/ot_smart/${ot.odoo_task_id}/assign`, {
        technician_id: ot.assigned_tech_id,
        scheduled_at: new Date(datetime).toISOString(),
        force_outside_shift: force,
        force_reason: force ? "Cambio de horario manual" : undefined,
      });
      toast.success("Horario actualizado");
      onUpdated?.();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("fuera de turno") || detail.includes("outside") || detail.includes("shift")) {
        setWarning(detail);
      } else {
        toast.error(detail || "Error al cambiar horario");
      }
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Cambio de horario" onClose={onClose} testid="modal-change-schedule">
      <div className="text-[10px] text-gray-500 mb-2">Técnico actual: <b>{ot.technician_name || "—"}</b></div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">Nueva fecha y hora</label>
      <input
        type="datetime-local" value={datetime} onChange={(e) => setDatetime(e.target.value)}
        className="w-full border rounded px-2 py-1.5 text-xs mb-3"
        data-testid="input-datetime"
      />
      {warning && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <div>{warning}</div>
            <button onClick={() => submit(true)} disabled={loading}
              className="mt-1.5 px-3 py-1 rounded bg-amber-600 text-white text-[10px] font-bold hover:bg-amber-700"
              data-testid="btn-force-schedule"
            >
              Asignar de todos modos
            </button>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border hover:bg-gray-50">Cancelar</button>
        <button onClick={() => submit(false)} disabled={loading}
          className="px-3 py-1.5 text-xs rounded bg-[#0A3747] text-white hover:bg-[#135166] disabled:opacity-50 flex items-center gap-1.5"
          data-testid="btn-confirm-schedule"
        >
          {loading && <Loader2 size={12} className="animate-spin" />} Cambiar horario
        </button>
      </div>
    </ModalShell>
  );
}

/* ─── Modal: Cambio de Estado (Carril) ─── */
function ChangeLaneModal({ ot, isAdminLevel, isProvisioned, onClose, onUpdated }) {
  const [selectedLane, setSelectedLane] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [cancelReasons, setCancelReasons] = useState([]);           // v101.188.0
  const [cancelReasonsReq, setCancelReasonsReq] = useState(false);
  const cancelMissing = selectedLane === "canceladas" && cancelReasonsReq && cancelReasons.length === 0;

  const submit = async () => {
    if (!selectedLane) return toast.error("Seleccione un carril");
    if (!reason.trim()) return toast.error(selectedLane === "canceladas" ? "Escriba el comentario" : "Motivo es requerido");
    // v101.97.0 · Cancelar exige un comentario real (mín. 10 caracteres)
    if (selectedLane === "canceladas" && reason.trim().length < 10)
      return toast.error("Para cancelar, escriba un comentario que explique el motivo (mínimo 10 caracteres)");
    if (cancelMissing) return toast.error("Seleccione el motivo de la cancelación");
    // v101.30.0 · Cancelar: solo se pide el comentario (sin confirmación adicional)

    setLoading(true);
    try {
      const { data } = await api.post(`/v1/ot_smart/${ot.odoo_task_id}/lane`, {
        lane: selectedLane, reason: reason.trim(),
        ...(selectedLane === "canceladas" && cancelReasons.length ? { cancel_reasons: cancelReasons } : {}),
      });
      toast.success(`Movida a "${selectedLane}" (${data.from_lane} → ${data.to_lane})`);
      onUpdated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar carril");
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Cambio de estado (carril)" onClose={onClose} testid="modal-change-lane" wide>
      <div className="space-y-1.5 mb-3">
        {LANES.map((lane) => {
          const disabled =
            lane.enabled === false ||
            (lane.enabled === "admin" && !isAdminLevel) ||
            (lane.enabled === "assign") || // "asignadas" requiere reasignar técnico
            (["nuevas", "revision", "canceladas"].includes(lane.key) && isProvisioned);
          const disabledReason =
            lane.enabled === false ? lane.desc :
            (lane.enabled === "admin" && !isAdminLevel) ? "Solo supervisor/admin" :
            lane.enabled === "assign" ? "Use 'Reasignar técnico' con fecha/hora" :
            isProvisioned ? "Cancele aprovisionamiento (y libere materiales si hay)" : null;
          const selected = selectedLane === lane.key;

          return (
            <button
              key={lane.key}
              onClick={() => !disabled && setSelectedLane(lane.key)}
              disabled={disabled}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-xs text-left transition-all ${
                disabled ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed" :
                selected ? "bg-[#0A3747] text-white border-[#0A3747]" :
                "bg-white text-gray-700 border-gray-200 hover:border-[#0A3747]/40"
              }`}
              data-testid={`lane-opt-${lane.key}`}
            >
              <div>
                <span className="font-semibold">{lane.label}</span>
                <span className="ml-2 text-[10px] opacity-70">{lane.desc}</span>
              </div>
              {disabled && (
                <span className="flex items-center gap-1 text-[9px]">
                  {lane.enabled === false ? <Ban size={10} /> : <Info size={10} />}
                  {disabledReason}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selectedLane === "canceladas" && (
        <div className="mb-2">
          <CancelReasonPicker odooTaskId={ot.odoo_task_id} value={cancelReasons}
            onChange={(codes, req) => { setCancelReasons(codes); setCancelReasonsReq(req); }} />
        </div>
      )}
      <label className="block text-xs font-semibold text-gray-600 mb-1">{selectedLane === "canceladas" ? "Comentario de la cancelación (obligatorio, mínimo 10 caracteres)" : "Motivo (obligatorio)"}</label>
      {selectedLane === "canceladas" ? (
        <textarea
          value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
          className="w-full border rounded px-2 py-1.5 text-xs mb-1" placeholder="Ej.: Cliente desiste del servicio; confirmado por teléfono"
          data-testid="input-lane-reason"
        />
      ) : (
        <input
          value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full border rounded px-2 py-1.5 text-xs mb-3" placeholder="Motivo del cambio de carril"
          data-testid="input-lane-reason"
        />
      )}
      {selectedLane === "canceladas" && (
        <div className={`text-[10px] mb-3 ${reason.trim().length >= 10 ? "text-emerald-600" : "text-red-500"}`}>
          {reason.trim().length}/10 caracteres mínimos
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border hover:bg-gray-50">Cancelar</button>
        <button onClick={submit} disabled={loading || !selectedLane || (selectedLane === "canceladas" && reason.trim().length < 10) || cancelMissing}
          className={`px-3 py-1.5 text-xs rounded text-white disabled:opacity-50 flex items-center gap-1.5 ${
            selectedLane === "canceladas" ? "bg-red-600 hover:bg-red-700" : "bg-[#0A3747] hover:bg-[#135166]"
          }`}
          data-testid="btn-confirm-lane"
        >
          {loading && <Loader2 size={12} className="animate-spin" />}
          {selectedLane === "canceladas" ? "Confirmar cancelación" : "Cambiar carril"}
        </button>
      </div>
    </ModalShell>
  );
}

/* ─── Shell de Modal ─── */
function ModalShell({ title, onClose, children, testid, wide }) {
  return (
    <BodyPortal>
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 pb-6 bg-black/40" onClick={onClose} data-testid={testid}>
      <div
        className={`bg-white rounded-2xl shadow-2xl p-5 ${wide ? "w-[480px]" : "w-[380px]"} max-h-[calc(100vh-5rem)] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[#0A3747]">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
    </BodyPortal>
  );
}
